
import { defineComponent, ref, watch, reactive } from 'vue'
import { ElForm } from 'element-plus'
import { AxiosResponse } from 'axios'

import InputLabel from '@/components/inputs/InputLabel.vue'
import JrDatePicker from '@/components/jr-parts/JrDatePicker.vue'
import JrFileInput from '@/components/jr-parts/JrFileInput.vue'
import JrTextarea from '@/components/jr-parts/JrTextarea.vue'
import JrRadioGroup from '@/components/jr-parts/JrRadioGroup.vue'
import JrFileView from '@/components/jr-parts/JrFileView.vue'
import { boundaryResultRule } from '@/constants/rules/boundary'
import { RequestHeaderBoundaryDetailForm, BoundaryResultForm } from '@/types/forms'
import { BoundaryResultType, FileTypes } from '@/constants/enums'
import { B_WN_CONFIRM, B_WN_DISORDER } from '@/constants/statuses'
import { ListRequest } from '@/requests/boundaryHistory'
import { BoundaryHistory } from '@/api'
import Logger from '@/services/logger'
import { useRouter } from 'vue-router'

export default defineComponent({
  name: 'BoundaryResult',
  emits: ['update:formRef', 'update:boundaryResult', 'update:fileInputRef'],
  components: {
    JrDatePicker,
    InputLabel,
    JrTextarea,
    JrFileInput,
    JrRadioGroup,
    JrFileView,
  },
  props: {
    // 申請基本ID
    requestHeaderId: {
      type: Number,
      required: true,
    },
    // 編集可能か
    isEdit: {
      type: Boolean,
      required: false,
      default: false,
    },
    //境界立会結果種別
    boundaryResultType: {
      type: Number,
      required: true,
    },
    modelValue: {
      type: Object as () => RequestHeaderBoundaryDetailForm,
      require: false,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: true,
    },
    messageId: {
      type: Number,
      require: false,
    },
  },
  setup(props, context) {
    const router = useRouter()
    const formRef = ref<InstanceType<typeof ElForm>>()
    const fileInputRef = ref<InstanceType<typeof JrFileInput>>()
    const form = reactive<BoundaryResultForm>({
      boundary_id: 0,
      preliminary_survey_remark: '',
      preliminary_survey_date: '',
      preliminary_survey_check_file: null,
      witness_date: '',
      witness_result_status: -1,
      witness_result_remark: '',
      witness_result_check_file: null,
    })

    watch(
      () => props.messageId,
      async (messageId: number | undefined) => {
        if (messageId) {
          const request = new ListRequest(messageId)
          await request.exec()
          if (request.success) {
            const { data } = request.getResponse() as AxiosResponse<Array<BoundaryHistory>>
            if (data.length == 0) {
              //履歴に紐つかないメッセージIDの場合は404ページに遷移
              Logger.info(`履歴に紐つかないメッセージIDです。${messageId}`)
              router.push({ name: 'NotFound' })
              return
            }

            form.preliminary_survey_date = data[0].preliminary_survey_date ?? ''
            form.preliminary_survey_remark = data[0].preliminary_survey_remark ?? ''
            form.preliminary_survey_check_file = data[0].preliminary_survey_check_file
            form.witness_date = data[0].witness_date ?? ''
            form.witness_result_status = data[0].witness_result_status ?? 0
            form.witness_result_remark = data[0].witness_result_remark ?? ''
            form.witness_result_check_file = data[0].witness_result_check_file
          }
        }
      },
      { immediate: true }
    )

    watch(
      () => props.modelValue,
      () => {
        if (props.modelValue) {
          form.boundary_id = props.modelValue.boundary.boundary_id as number
          form.preliminary_survey_remark = props.modelValue.boundary.preliminary_survey_remark ?? ''
          form.preliminary_survey_date = props.modelValue.boundary.preliminary_survey_date ?? ''
          form.preliminary_survey_check_file = props.modelValue.boundary.preliminary_survey_check_file
          form.witness_date = props.modelValue.boundary.witness_date ?? ''
          form.witness_result_status = props.modelValue.boundary.witness_result_status ?? 0
          form.witness_result_remark = props.modelValue.boundary.witness_result_remark ?? ''
          form.witness_result_check_file = props.modelValue.boundary.witness_result_check_file
        }
      },
      { immediate: true }
    )

    watch(
      () => form,
      () => {
        context.emit('update:boundaryResult', form)
      },
      { deep: true }
    )

    watch(
      () => formRef,
      () => {
        context.emit('update:formRef', formRef)
      },
      { deep: true }
    )

    watch(fileInputRef, () => {
      context.emit('update:fileInputRef', fileInputRef)
    })

    return {
      form,
      formRef,
      BoundaryResultType,
      boundaryResultRule,
      FileTypes,
      fileInputRef,
      B_WN_CONFIRM,
      B_WN_DISORDER,
    }
  },
})
